import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import styles from "./ScrollStack.module.css";
import { smoothScrollTo } from "../utils/smoothScrollTo";

interface ScrollStackProps {
  images: string[];
  offset?: number;
  children?: React.ReactNode;
  onScrollEnd?: () => void;
  onScrollHide?: () => void;
  titleAnchorSelector?: string;
  headerOffset?: number;
  credits?: Record<number, string>;
}

const MOBILE_BREAKPOINT = 700;
const MAX_SHRINK = 0.2;
const CUE_HIDE_BEFORE_LAST_VH = 0.2;

const ScrollStack: React.FC<ScrollStackProps> = ({
  images,
  offset = 5,
  children,
  onScrollEnd,
  onScrollHide,
  titleAnchorSelector = "h1, h2, [data-desc-title], .descTitle, .sectionTitle",
  headerOffset,
  credits,
}) => {
  const [vh, setVh] = useState(() => window.innerHeight);
  const [descHeight, setDescHeight] = useState(0);
  const descRef = useRef<HTMLDivElement>(null);
  const cueRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
  const [mobileIdx, setMobileIdx] = useState(0);

  const getCredit = (i: number) => (credits && credits[i] ? credits[i] : undefined);

  const hasScrollCue = !isMobile && images.length > 1;

  useEffect(() => {
    const onResize = () => {
      setVh(window.innerHeight);
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
      if (descRef.current) setDescHeight(descRef.current.getBoundingClientRect().height);
    };
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (descRef.current) setDescHeight(descRef.current.getBoundingClientRect().height);
  }, [children]);


  useEffect(() => {
    if (isMobile) return;
    if (!Array.isArray(images) || images.length === 0) return;

    let triggered = false;

    const handleScroll = () => {
      const y = window.scrollY;

      const lastFrameStartY = (images.length - 1) * vh;
      const descStartY = images.length * vh;
      const endY = descStartY - window.innerHeight * 0.5;

      if (hasScrollCue && cueRef.current) {
        const cueHideAt =
          images.length > 1
            ? lastFrameStartY - vh * CUE_HIDE_BEFORE_LAST_VH
            : descStartY - vh * CUE_HIDE_BEFORE_LAST_VH;

        const cueHideAtClamped = Math.max(0, cueHideAt);
        cueRef.current.style.opacity = y >= cueHideAtClamped ? "0" : "1";
      }

      if (!triggered && y >= endY) {
        triggered = true;
        onScrollEnd?.();
      }
      if (triggered && y < endY - 1) {
        triggered = false;
        onScrollHide?.();
      }

      images.forEach((_, i) => {
        const card = cardRefs.current[i];
        if (!card) return;

        const startY = i * vh;
        const endYi = (i + 1) * vh;
        const initO = i * offset;

        let tY = 0,
          scale = 1;

        if (y < startY) {
          tY = 0;
        } else if (y >= endYi) {
          tY = vh;
          scale = 1 - MAX_SHRINK;
        } else {
          const p = (y - startY) / vh;
          tY = p * vh;
          scale = 1 - Math.min(MAX_SHRINK, p * MAX_SHRINK);
        }

        card.style.transform = `translate3d(-50%, calc(-50% + ${initO}px - ${tY}px), 0) scale(${scale})`;
        card.style.zIndex = `${images.length - i}`;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [images, offset, vh, isMobile, onScrollEnd, onScrollHide, hasScrollCue]);

const scrollToDescription = () => {
  if (isMobile) return;

  const container = descRef.current;
  const anchor =
    container?.querySelector<HTMLElement>(titleAnchorSelector) || container;
  if (!anchor) return;

  const rect = anchor.getBoundingClientRect();

  const rootStyles = getComputedStyle(document.documentElement);
  const navH =
    parseFloat(rootStyles.getPropertyValue("--navbar-h")) || 80;

  const extra = typeof headerOffset === "number" ? headerOffset : 0;
  const offset = navH + extra;

  const y = window.scrollY + rect.top - offset;

  const prefersReduced =
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;

  smoothScrollTo(y, 1800);
};


  const onCueKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      scrollToDescription();
    }
  };

  if (isMobile) {
    const prev = () => setMobileIdx((i) => (i <= 0 ? images.length - 1 : i - 1));
    const next = () => setMobileIdx((i) => (i >= images.length - 1 ? 0 : i + 1));

    return (
      <>
        <div className={styles.mobileCarousel}>
          <div className={styles.slides} style={{ transform: `translateX(-${mobileIdx * 100}%)` }}>
            {images.map((src, i) => (
              <div key={i} className={styles.slide}>
                <img src={src} alt={`slide-${i}`} className={styles.slideImage} />
                {getCredit(i) && (
                  <span className={`${styles.credit} ${styles.creditMobile}`} aria-hidden="true">
                    {getCredit(i)}
                  </span>
                )}
              </div>
            ))}
          </div>

          <button className={styles.arrowPrev} onClick={prev} aria-label="Prev">
            <ArrowLeft className={styles.icon} />
          </button>
          <button className={styles.arrowNext} onClick={next} aria-label="Next">
            <ArrowRight className={styles.icon} />
          </button>

          <div className={styles.pagerWrap} role="status" aria-live="polite">
            <div className={styles.counter}>
              {mobileIdx + 1} / {images.length}
            </div>
            <div className={styles.progressTrack} aria-hidden>
              <div
                className={styles.progressBar}
                style={{ width: `${Math.round(((mobileIdx + 1) / images.length) * 100)}%` }}
              />
            </div>
          </div>
        </div>
        <div ref={descRef} className={styles.mobileDescription}>
          {children}
        </div>
      </>
    );
  }

  const totalHeight = Math.max(0, images.length * vh + descHeight);

  return (
    <div className={styles.wrapper} style={{ height: totalHeight }}>
      {images.map((src, i) => (
        <div
          key={i}
          ref={(el) => (cardRefs.current[i] = el)}
          className={styles.scrollCard}
        >
          <div className={styles.imageWrap}>
            <img src={src} alt={`slide-${i}`} className={styles.image} />
          </div>

          {getCredit(i) && (
            <span className={styles.credit} aria-hidden="true">
              {getCredit(i)}
            </span>
          )}
        </div>
      ))}

      {hasScrollCue && (
        <div
          ref={cueRef}
          className={styles.scrollCue}
          onClick={scrollToDescription}
          onKeyDown={onCueKeyDown}
          role="button"
          tabIndex={0}
          aria-label="Zum Projekttext scrollen"
        >
          <span className={styles.mouseShell} aria-hidden>
            <span className={styles.mouseWheel} />
          </span>
          <span className={styles.cueTrail} aria-hidden />
        </div>
      )}

      <div ref={descRef} className={styles.descriptionWrapper} style={{ top: images.length * vh }}>
        {children}
      </div>
    </div>
  );
};

export default ScrollStack;
